import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import uniq from "lodash/uniq";
import { PDPv2 } from "./PDPv2";
import { useCognitoUser } from "~/features/user/useCognitoUser";
import { useSellableRights } from "~/features/user/useSellableRights";
import { useUserAccount } from "~/features/user/useUserAccount";
import {
    getHasUnlockableContent,
    getIsAdminUser,
    getIsOwnerUnlockableContent,
    getTokenRights,
    loadToken,
    loadTokenMarketData,
    searchUsersByWallets,
    setLoading,
    trackHubSpotEvent,
    trackSegmentEvent,
} from "~/api/choreography";
import { LoadingSlots, Token } from "~/api/data-schema";
import { useParentToken } from "~/features/artwork/useParentToken";
import { useEditionToken } from "~/features/artwork/useEditionToken";
import { useEditionNumber } from "~/features/artwork/useEditionNumber";
import { useTokenMarketData } from "~/features/tokenMarketData/useTokenMarketData";
import { useContractAddress } from "~/features/artwork/useContractAddress";
import {
    ArtworkEngagementEventTypes,
    EventNames,
} from "~/api/hubspot/hubspotEvents";
import { getUtms } from "~/api/hubspot/hubspot";
import { SegmentEvents } from "~/api/segment/segmentEvents";
import { useCurrentTokenContractDetails } from "~/features/solidityContracts/useCurrentTokenContractDetails";

export function PdpPage({
    blockchainId,
    tokenId,
    tokenContractAddress,
}: {
    blockchainId: number;
    tokenId: number;
    tokenContractAddress: string;
}) {
    const dispatch = useDispatch();
    const userAccount = useUserAccount();
    const user = useCognitoUser();
    const tokenContractDetails = useCurrentTokenContractDetails();

    useEffect(() => {
        if (tokenId && blockchainId) {
            dispatch(loadTokenMarketData({ tokenId, networkId: blockchainId }));
        }
        dispatch(
            setLoading({
                slot: LoadingSlots.LOAD_MARKET_DATA,
                loading: true,
            }),
        );
    }, [blockchainId, dispatch, tokenId]);

    useEffect(() => {
        if (tokenId && blockchainId && tokenContractAddress) {
            dispatch(
                loadToken({
                    tokenId,
                    networkId: blockchainId,
                    tokenContractAddress: tokenContractAddress as string,
                }),
            );
            dispatch(getTokenRights(tokenId));
        }
    }, [blockchainId, dispatch, tokenContractAddress, tokenId]);

    useEffect(() => {
        if (tokenId) {
            if (userAccount) {
                dispatch(getIsAdminUser({ userAccount, tokenContractDetails }));
            }
            dispatch(getHasUnlockableContent(tokenId));
            dispatch(getIsOwnerUnlockableContent(tokenId));
        }
    }, [userAccount, dispatch, tokenId, tokenContractDetails]);

    const clientParentToken = useParentToken({
        tokenId,
        tokenContractAddress: tokenContractAddress as string,
    });

    const parentToken =
        clientParentToken || ({ editions: [] } as unknown as Token);
    const { editions = [] } = parentToken;
    const token = useEditionToken(parentToken, tokenId);
    const { metadata } = token || {};
    const editionNumber = useEditionNumber(parentToken, tokenId);

    const sellableRights = useSellableRights();

    const clientTokenMarketData = useTokenMarketData();

    const tokenMarketData = clientTokenMarketData?.[tokenId] || {
        auctions: [],
        buyNowDeals: [],
        transfers: [],
    };
    const {
        auctions: [auction],
        buyNowDeals: [buyNowDeal],
        tokenBids,
        transfers,
    } = tokenMarketData;

    const contractAddress = useContractAddress();

    const wallets = uniq(
        (sellableRights || [])
            .map((policy) => policy.permission.wallet.toString())
            .concat(token?.erc721Owner || "nonExistingWallet"),
    );

    const biddersWallet = uniq(
        (tokenBids || []).map((tokenBid) => tokenBid.bidder),
    );

    useEffect(() => {
        dispatch(searchUsersByWallets([...wallets, ...biddersWallet]));
    }, [dispatch, wallets, biddersWallet]);

    useEffect(() => {
        if (tokenId && blockchainId && metadata) {
            dispatch(
                trackHubSpotEvent({
                    eventName: EventNames.artwork_engagement,
                    email: user?.email,
                    properties: {
                        user_id: user?.externalId,
                        action_type: ArtworkEngagementEventTypes.artwork_viewed,
                        page_path: globalThis?.location?.pathname,
                        page_title: globalThis?.document.title,
                        page_url: globalThis?.location?.href,
                        artwork_id: tokenId,
                        artwork_name: metadata?.name,
                        artwork_type:
                            metadata.properties.tokenType?.description,
                        ...getUtms(),
                    },
                }),
            );
            dispatch(
                trackSegmentEvent({
                    eventName: SegmentEvents.ARTWORKS_PAGE_VIEWED,
                    properties: {
                        user_id: user?.externalId,
                        email: user?.email,
                        artwork_id: tokenId,
                        artwork_name: metadata?.name,
                        artwork_type:
                            metadata.properties.tokenType?.description,
                        artist_name:
                            metadata.properties.artistName?.description,
                    },
                }),
            );
        }
    }, [blockchainId, dispatch, metadata, tokenId, user]);

    return (
        <PDPv2
            tokenBids={tokenBids}
            token={token}
            buyNowDeal={buyNowDeal}
            auction={auction}
            sellableRights={sellableRights}
            transfers={transfers}
            metadata={metadata}
            editions={editions}
            editionNumber={editionNumber}
            tokenMarketData={tokenMarketData}
            contractAddress={contractAddress || ""}
            parentToken={parentToken}
        />
    );
}
